/* ============================================================
   The browser, as the client core sees it: ONE adapters object, handed to
   <ClientCoreProvider> in App.tsx. Everything that only a browser can answer
   lives behind this directory. (The Expo app's counterpart is
   app/src/platform/.)

   Not provided, deliberately: update signals (a web deploy is the update).
   Billing is RevenueCat Web Billing (./billing.ts) — present whenever the
   build carries a key, so the entitlement context gets the same grant-only
   backstop the phone has. Analytics is GA4 through gtag (../analytics.ts),
   present whenever the build carries a measurement id and otherwise a no-op.
   ============================================================ */
import { type ClientCoreAdapters, createApiClient } from '@habishine/client-core';

import { identify as identifyAnalytics, track } from '../analytics';
import { API_URL, clientConfig } from '../config';
import { deregisterFromPush } from '../push';
import { billing, billingAvailable } from './billing';
import { lifecycle } from './lifecycle';
import { oauth } from './oauth';
import { storage } from './storage';
import { tokenStorage } from './tokenStorage';

// How long sign-out waits to detach this browser from the account's pushes
// before going ahead anyway (the mobile app uses the same bound).
const PUSH_RELEASE_TIMEOUT_MS = 3_000;

export const adapters: ClientCoreAdapters = {
  storage,
  tokenStorage,
  oauth,
  lifecycle,
  analytics: { track, identify: identifyAnalytics },
  // Absent (not a stub) without a key, so the core's backstop never asks a
  // vendor that was never configured.
  ...(billingAvailable ? { billing } : {}),
  auth: {
    /** Drop this browser's push subscription while the session can still
        authenticate the DELETE — otherwise the account's digests keep arriving
        here after sign-out. Time-boxed and best-effort, as on mobile. */
    async beforeSignOut(getIdToken) {
      const release = deregisterFromPush(
        createApiClient({ baseUrl: API_URL, getIdToken, headers: clientConfig.clientHeaders }),
      );
      await Promise.race([
        release,
        new Promise<void>((resolve) => setTimeout(resolve, PUSH_RELEASE_TIMEOUT_MS)),
      ]);
    },
  },
};
